import { lessonTypeLabels } from "@src/constants";

/**
 * Where a standalone lesson cover card is rendered. The purple top bar reads
 * differently per surface for Australian lessons (OP-3134).
 */
export type LessonCardContext = "lessonLibrary" | "wellbeingFoundations" | "program";

/** SuperAdmin Edit Lesson "Lesson Type" codes (see lessonTypeOptions). */
export const LESSON_TYPE_CODE = {
  STUDENT_VOICE: "0",
  POSITIVE_PATHWAYS: "1",
  BRAIN_BREAK: "2",
  SKILL_BUILDER: "3",
  RESOURCE: "5",
} as const;

const { STUDENT_VOICE, POSITIVE_PATHWAYS, BRAIN_BREAK, SKILL_BUILDER, RESOURCE } = LESSON_TYPE_CODE;

/** NA cards show the SuperAdmin label for these types; everything else has no bar. */
const NA_BAR_TYPES: string[] = [STUDENT_VOICE, POSITIVE_PATHWAYS, BRAIN_BREAK, SKILL_BUILDER, RESOURCE];

/**
 * Text for the purple bar on an Australian standalone lesson card, or null when
 * the bar is hidden and only the duration pill shows.
 *
 * - Lesson Library: every typed lesson shows its type. Positive Pathways reads
 *   "Standalone".
 * - Wellbeing Foundations (Brain Breaks / Skill-Builders tabs): no bar, duration
 *   only, matching the Student Introduction cards. Resource still reads "Resource".
 * - Any other program: Positive Pathways has no bar; Resource reads "Resource";
 *   Brain Break / Skill-Builder show their type.
 */
export function resolveAuLessonCardBarLabel(
  lessonType: string | null | undefined,
  context: LessonCardContext,
): string | null {
  if (!lessonType) return null;
  const label = lessonTypeLabels[lessonType as keyof typeof lessonTypeLabels];
  if (!label) return null;

  if (lessonType === RESOURCE) return label;

  if (lessonType === POSITIVE_PATHWAYS) {
    return context === "lessonLibrary" ? "Standalone" : null;
  }

  if (lessonType === BRAIN_BREAK || lessonType === SKILL_BUILDER) {
    return context === "wellbeingFoundations" ? null : label;
  }

  // Student Voice: keep the existing label behaviour (no AU standalone lessons today).
  if (lessonType === STUDENT_VOICE) return label;

  return null;
}

/** Text for the purple bar on a North America card, unchanged from before OP-3134. */
export function resolveNaLessonCardBarLabel(
  lessonType: string | null | undefined,
): string | null {
  if (!lessonType || NA_BAR_TYPES.indexOf(lessonType) === -1) return null;
  return lessonTypeLabels[lessonType as keyof typeof lessonTypeLabels] ?? null;
}

export function resolveLessonCardBarLabel(args: {
  lessonType: string | null | undefined;
  isAUuser: boolean;
  context: LessonCardContext;
}): string | null {
  const { lessonType, isAUuser, context } = args;
  return isAUuser
    ? resolveAuLessonCardBarLabel(lessonType, context)
    : resolveNaLessonCardBarLabel(lessonType);
}
